import { zodResolver } from '@hookform/resolvers/zod'
import { Modal } from 'antd'
import { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import { NULL_UUID_ID } from '@/constants'
import { categorySchema, nomenclatureGroupSchema } from '@/schemas'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  AntdTreeType,
  CategoryInterface,
  NomenclatureGroupInterface,
} from '@/types'
import api from '@/utils/api/api'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import { isCatalogApprover } from '@/utils/catalog/approvalStatus'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'
import zodErrorModalMessage from '@/utils/forms/zodErrorModalMessage'
import convertToAntdFormatTree from '@/utils/tree/antd/convertToAntdFormatTree'
import handleSelectTreeNode from '@/utils/tree/selection/handleSelectTreeNode'

import {
  treeEntityCreationSchema,
  type TreeEntityCreationType,
} from './TreeEntityCreationModal.config'
import cn from './TreeEntityCreationModal.module.scss'

export type TreeEntityCreatedResult = {
  id?: string
  code?: number
  description: string
  status?: 'DRAFT' | 'APPROVED' | 'REJECTED'
  priority?: number
  treeType: 'categories' | 'nomenclature-groups'
  parentId: string
}

type UseTreeEntityCreationFormParamsType = {
  active: boolean
  treeType: 'categories' | 'nomenclature-groups'
  allowUnapprovedSelection?: boolean
  defaultParentId?: string
  externalTree?: CategoryInterface[] | NomenclatureGroupInterface[]
  setExternalAntdTree?: (externalAntdTree: AntdTreeType[]) => void
  setExternalTree?:
    | ((externalTree: CategoryInterface[]) => void)
    | ((externalTree: NomenclatureGroupInterface[]) => void)
  onSuccess?: (created?: TreeEntityCreatedResult) => void
}

export default function useTreeEntityCreationForm(
  params: UseTreeEntityCreationFormParamsType
) {
  const {
    active,
    treeType,
    allowUnapprovedSelection = false,
    defaultParentId,
    externalTree,
    setExternalAntdTree,
    setExternalTree,
    onSuccess,
  } = params

  const [antdTree, setAntdTree] = useState<AntdTreeType[]>([])
  const [tree, setTree] = useState([])
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false)
  const [pathToSelectedNode, setPathToSelectedNode] = useState<string>('')

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    control,
    watch,
    setValue,
  } = useForm<TreeEntityCreationType>({
    shouldFocusError: false,
    resolver: zodResolver(treeEntityCreationSchema),
    defaultValues: {
      approveImmediately: true,
      parentId: defaultParentId,
    },
  })

  const [modal, contextHolder] = Modal.useModal()
  const currentUser = useUserInfoStore((state) => state.currentUser)
  const isApprover = isCatalogApprover(currentUser?.roles)

  useEffect(() => {
    if (externalTree) return

    if (active && !tree.length) {
      api
        .get(
          treeType === 'categories'
            ? '/categories/root-trees'
            : '/main/nomenclature-groups/root-trees'
        )
        .then((response) => {
          const treeData = response.data.trees || response.data
          setTree(treeData)
        })
        .catch((error) => {
          if (error instanceof ZodError) {
            zodErrorModalMessage(modal)
          }
          handleApiError(error, modal)
        })
    }
  }, [active, treeType, externalTree, tree.length, modal])

  useEffect(() => {
    const sourceTree = externalTree || tree

    if (sourceTree && sourceTree.length) {
      try {
        const validatedTree = (
          treeType === 'categories' ? categorySchema : nomenclatureGroupSchema
        )
          .array()
          .parse(sourceTree)

        const converted = convertToAntdFormatTree({
          nodes: validatedTree,
          classNames: cn,
          allSelectable: true,
          inTreeSelect: true,
          respectApprovalStatus:
            treeType === 'categories' ? !allowUnapprovedSelection : true,
        })
        setAntdTree(converted)
      } catch (e) {
        console.error('Validation error in tree entity creation form:', e)
      }
    } else {
      setAntdTree((prev) => (prev.length ? [] : prev))
    }
  }, [externalTree, tree, treeType, allowUnapprovedSelection])

  const handleChangeTreeSelect = (
    currentTree: CategoryInterface[] | NomenclatureGroupInterface[],
    node: string
  ) => {
    const activeTree = externalTree || tree
    const path = handleSelectTreeNode(activeTree, node)
    if (path) setPathToSelectedNode(path)
  }

  const handleClearTreeSelect = () => {
    setPathToSelectedNode('')
  }

  const handleTreeEntityCreation = (data: TreeEntityCreationType) => {
    setConfirmLoading(true)

    const payload = {
      parentId: data.parentId ? data.parentId : NULL_UUID_ID,
      description: data.description,
      ...(treeType === 'categories' && isApprover
        ? { status: data.approveImmediately ? 'APPROVED' : 'DRAFT' }
        : {}),
    }

    const endpoint =
      treeType === 'categories' ? '/categories' : '/main/nomenclature-groups'

    return api
      .post(endpoint, payload)
      .then((response) => {
        setConfirmLoading(false)

        if (setExternalAntdTree) setExternalAntdTree([])
        if (setExternalTree) setExternalTree([])
        setPathToSelectedNode('')
        reset()

        toast.success(
          `${treeType === 'categories' ? 'Категория' : 'Группа номенклатуры'} успешно создана`
        )

        const responseData = response.data as {
          category?: {
            id?: string
            code?: number
            description?: string
            status?: 'DRAFT' | 'APPROVED' | 'REJECTED'
            priority?: number
            parent?: { id?: string | null }
          }
          nomenclatureGroup?: {
            id?: string
            code?: number
            description?: string
            parentId?: string
          }
        }

        const createdId =
          treeType === 'categories'
            ? responseData.category?.id
            : responseData.nomenclatureGroup?.id
        const createdCode =
          treeType === 'categories'
            ? responseData.category?.code
            : responseData.nomenclatureGroup?.code
        const createdDescription =
          treeType === 'categories'
            ? responseData.category?.description
            : responseData.nomenclatureGroup?.description
        const createdParentId =
          treeType === 'categories'
            ? responseData.category?.parent?.id
            : responseData.nomenclatureGroup?.parentId

        onSuccess?.({
          id: createdId,
          code: createdCode,
          description: createdDescription ?? data.description,
          status:
            treeType === 'categories'
              ? responseData.category?.status
              : undefined,
          priority:
            treeType === 'categories'
              ? responseData.category?.priority
              : undefined,
          treeType,
          parentId: createdParentId ?? payload.parentId,
        })
      })
      .catch((e) => {
        setConfirmLoading(false)
        if (e instanceof AxiosError) {
          handleApiError(e, modal)
        } else {
          toast.error('Неизвестная ошибка')
        }
      })
  }

  const submit = async () => {
    await handleSubmit(
      handleTreeEntityCreation,
      handleFormSubmitError(isValid)
    )()
  }

  const cancel = () => {
    reset()
    setPathToSelectedNode('')
  }

  return {
    register,
    control,
    errors,
    watch,
    setValue,
    tree: externalTree || tree,
    antdTree,
    pathToSelectedNode,
    confirmLoading,
    isApprover,
    handleChangeTreeSelect,
    handleClearTreeSelect,
    submit,
    cancel,
    modal,
    contextHolder,
  }
}
